<template>
  <button
    :class="buttonClasses"
    :disabled="disabled || loading"
    :type="htmlType"
    @click="handleClick"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @focus="handleFocus"
    @blur="handleBlur"
  >
    <!-- 加载图标 -->
    <Icon
      v-if="loading"
      name="loading"
      class="btn-loading-icon"
      :spin="true"
    />

    <!-- 前置图标 -->
    <Icon
      v-else-if="icon && iconPosition === 'left'"
      :name="icon"
      class="btn-icon btn-icon-left"
    />

    <!-- 按钮内容 -->
    <span v-if="$slots.default || text" class="btn-content">
      <slot>{{ text }}</slot>
    </span>

    <!-- 后置图标 -->
    <Icon
      v-if="!loading && icon && iconPosition === 'right'"
      :name="icon"
      class="btn-icon btn-icon-right"
    />
  </button>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import Icon from '../Icon/index.vue'

/**
 * 按钮类型
 */
export type ButtonType = 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info' | 'text' | 'link'

/**
 * 按钮尺寸
 */
export type ButtonSize = 'mini' | 'small' | 'medium' | 'large'

/**
 * 按钮形状
 */
export type ButtonShape = 'default' | 'round' | 'circle'

/**
 * 图标位置
 */
export type IconPosition = 'left' | 'right'

/**
 * HTML按钮类型
 */
export type HtmlType = 'button' | 'submit' | 'reset'

/**
 * 按钮Props接口
 */
interface ButtonProps {
  /** 按钮类型 */
  type?: ButtonType
  /** 按钮尺寸 */
  size?: ButtonSize
  /** 按钮形状 */
  shape?: ButtonShape
  /** 是否禁用 */
  disabled?: boolean
  /** 是否加载中 */
  loading?: boolean
  /** 是否为块级按钮 */
  block?: boolean
  /** 是否为幽灵按钮 */
  ghost?: boolean
  /** 是否为危险按钮 */
  danger?: boolean
  /** 按钮文本 */
  text?: string
  /** 图标名称 */
  icon?: string
  /** 图标位置 */
  iconPosition?: IconPosition
  /** HTML按钮类型 */
  htmlType?: HtmlType
  /** 自定义类名 */
  customClass?: string
  /** 是否自动聚焦 */
  autofocus?: boolean
}

// Props定义
const props = withDefaults(defineProps<ButtonProps>(), {
  type: 'primary',
  size: 'medium',
  shape: 'default',
  disabled: false,
  loading: false,
  block: false,
  ghost: false,
  danger: false,
  iconPosition: 'left',
  htmlType: 'button',
  autofocus: false
})

const emit = defineEmits<ButtonEmits>()

// Emits定义
interface ButtonEmits {
  click: [event: MouseEvent]
  mouseenter: [event: MouseEvent]
  mouseleave: [event: MouseEvent]
  focus: [event: FocusEvent]
  blur: [event: FocusEvent]
}

// 响应式状态
const isHovered = ref(false)
const isFocused = ref(false)

// 计算属性
const buttonClasses = computed(() => {
  const classes = [
    'dh-button',
    `dh-button--${props.type}`,
    `dh-button--${props.size}`,
    `dh-button--${props.shape}`
  ]

  if (props.disabled) classes.push('dh-button--disabled')
  if (props.loading) classes.push('dh-button--loading')
  if (props.block) classes.push('dh-button--block')
  if (props.ghost) classes.push('dh-button--ghost')
  if (props.danger) classes.push('dh-button--danger')
  if (isHovered.value) classes.push('dh-button--hovered')
  if (isFocused.value) classes.push('dh-button--focused')
  if (props.customClass) classes.push(props.customClass)

  return classes
})

// 事件处理
const handleClick = (event: MouseEvent) => {
  if (props.disabled || props.loading) return
  emit('click', event)
}

const handleMouseEnter = (event: MouseEvent) => {
  isHovered.value = true
  emit('mouseenter', event)
}

const handleMouseLeave = (event: MouseEvent) => {
  isHovered.value = false
  emit('mouseleave', event)
}

const handleFocus = (event: FocusEvent) => {
  isFocused.value = true
  emit('focus', event)
}

const handleBlur = (event: FocusEvent) => {
  isFocused.value = false
  emit('blur', event)
}
</script>

<style lang="scss" scoped>
.dh-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  text-align: center;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
  outline: none;

  &:focus-visible {
    box-shadow: 0 0 0 2px var(--color-primary-light);
  }

  // 按钮类型样式
  &--primary {
    background-color: var(--color-primary);
    border-color: var(--color-primary);
    color: white;

    &:hover:not(.dh-button--disabled):not(.dh-button--loading) {
      background-color: var(--color-primary-hover);
      border-color: var(--color-primary-hover);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(var(--color-primary-rgb), 0.3);
    }

    &:active:not(.dh-button--disabled):not(.dh-button--loading) {
      background-color: var(--color-primary-active);
      border-color: var(--color-primary-active);
      transform: translateY(0);
    }
  }

  &--secondary {
    background-color: var(--color-bg-container);
    border-color: var(--color-border);
    color: var(--color-text-primary);

    &:hover:not(.dh-button--disabled):not(.dh-button--loading) {
      border-color: var(--color-primary);
      color: var(--color-primary);
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
  }

  &--success {
    background-color: var(--color-success);
    border-color: var(--color-success);
    color: white;

    &:hover:not(.dh-button--disabled):not(.dh-button--loading) {
      background-color: var(--color-success-hover);
      border-color: var(--color-success-hover);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(var(--color-success-rgb), 0.3);
    }
  }

  &--warning {
    background-color: var(--color-warning);
    border-color: var(--color-warning);
    color: white;

    &:hover:not(.dh-button--disabled):not(.dh-button--loading) {
      background-color: var(--color-warning-hover);
      border-color: var(--color-warning-hover);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(var(--color-warning-rgb), 0.3);
    }
  }

  &--danger {
    background-color: var(--color-danger);
    border-color: var(--color-danger);
    color: white;

    &:hover:not(.dh-button--disabled):not(.dh-button--loading) {
      background-color: var(--color-danger-hover);
      border-color: var(--color-danger-hover);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(var(--color-danger-rgb), 0.3);
    }
  }

  &--info {
    background-color: var(--color-info);
    border-color: var(--color-info);
    color: white;

    &:hover:not(.dh-button--disabled):not(.dh-button--loading) {
      background-color: var(--color-info-hover);
      border-color: var(--color-info-hover);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(var(--color-info-rgb), 0.3);
    }
  }

  &--text {
    background-color: transparent;
    border-color: transparent;
    color: var(--color-text-primary);

    &:hover:not(.dh-button--disabled):not(.dh-button--loading) {
      background-color: var(--color-bg-hover);
      color: var(--color-primary);
    }
  }

  &--link {
    background-color: transparent;
    border-color: transparent;
    color: var(--color-primary);
    text-decoration: underline;

    &:hover:not(.dh-button--disabled):not(.dh-button--loading) {
      color: var(--color-primary-hover);
    }
  }

  // 尺寸样式
  &--mini {
    padding: 4px 8px;
    font-size: 12px;
    border-radius: 4px;

    .btn-icon {
      font-size: 12px;
    }
  }

  &--small {
    padding: 6px 12px;
    font-size: 13px;
    border-radius: 5px;

    .btn-icon {
      font-size: 13px;
    }
  }

  &--medium {
    padding: 8px 16px;
    font-size: 14px;
    border-radius: 6px;

    .btn-icon {
      font-size: 14px;
    }
  }

  &--large {
    padding: 12px 24px;
    font-size: 16px;
    border-radius: 8px;

    .btn-icon {
      font-size: 16px;
    }
  }

  // 形状样式
  &--round {
    border-radius: 100px;
  }

  &--circle {
    width: 40px;
    height: 40px;
    padding: 0;
    border-radius: 50%;

    &.dh-button--mini {
      width: 24px;
      height: 24px;
    }

    &.dh-button--small {
      width: 32px;
      height: 32px;
    }

    &.dh-button--large {
      width: 48px;
      height: 48px;
    }

    .btn-content {
      display: none;
    }
  }

  // 状态样式
  &--disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
  }

  &--loading {
    cursor: default;

    .btn-loading-icon {
      animation: spin 1s linear infinite;
    }
  }

  &--block {
    display: flex;
    width: 100%;
  }

  &--ghost {
    background-color: transparent;

    &.dh-button--primary {
      color: var(--color-primary);
      border-color: var(--color-primary);

      &:hover:not(.dh-button--disabled):not(.dh-button--loading) {
        background-color: var(--color-primary);
        color: white;
      }
    }

    &.dh-button--success {
      color: var(--color-success);
      border-color: var(--color-success);

      &:hover:not(.dh-button--disabled):not(.dh-button--loading) {
        background-color: var(--color-success);
        color: white;
      }
    }

    &.dh-button--warning {
      color: var(--color-warning);
      border-color: var(--color-warning);

      &:hover:not(.dh-button--disabled):not(.dh-button--loading) {
        background-color: var(--color-warning);
        color: white;
      }
    }

    &.dh-button--danger {
      color: var(--color-danger);
      border-color: var(--color-danger);

      &:hover:not(.dh-button--disabled):not(.dh-button--loading) {
        background-color: var(--color-danger);
        color: white;
      }
    }
  }

  &--danger:not(.dh-button--ghost) {
    background-color: var(--color-danger);
    border-color: var(--color-danger);
    color: white;
  }
}

.btn-icon {
  display: inline-flex;
  align-items: center;

  &-left {
    margin-right: 2px;
  }

  &-right {
    margin-left: 2px;
  }
}

.btn-content {
  display: inline-flex;
  align-items: center;
}

// 动画
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

// 暗色主题适配
.dark {
  .dh-button {
    &--secondary {
      background-color: var(--color-bg-container-dark);
      border-color: var(--color-border-dark);
      color: var(--color-text-primary-dark);

      &:hover:not(.dh-button--disabled):not(.dh-button--loading) {
        border-color: var(--color-primary);
        color: var(--color-primary);
      }
    }

    &--text {
      color: var(--color-text-primary-dark);

      &:hover:not(.dh-button--disabled):not(.dh-button--loading) {
        background-color: var(--color-bg-hover-dark);
      }
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .dh-button {
    &--large {
      padding: 10px 20px;
      font-size: 15px;
    }

    &--medium {
      padding: 7px 14px;
      font-size: 13px;
    }
  }
}

@media (max-width: 480px) {
  .dh-button {
    &--block {
      padding: 12px 16px;
    }
  }
}
</style>
